import { formatCnpj, formatCpf, isValidCnpj, isValidCpf, normalizeCnpj, normalizeCpf, } from '@aeris/organization';
/**
 * Parties.
 *
 * Master Specification section 10 is unusually direct about the shape of this
 * module, and about the mistake it is preventing:
 *
 *   > Não duplicar dados criando cadastros independentes sem necessidade.
 *   > Utilizar entidade central conceitualmente denominada Party.
 *   > A mesma organização pode possuir múltiplos papéis.
 *
 * So there is one `Party` carrying a SET of roles, not a `Customer` table and a
 * `Supplier` table. The example the specification itself gives — a company that
 * is both — is not an edge case in a Brazilian distributor: the shop that buys
 * your parts also sells you its own, and the carrier that delivers is invoiced
 * like any other supplier.
 *
 * The cost of getting this wrong is not duplicated typing. It is that a
 * customer and a supplier row for the same CNPJ drift: the address is updated
 * on one, the trade name on the other, and by the time somebody notices there
 * is no answer to "which is right" that does not involve a human reading both.
 * Worse, the financial module then has two counterparties where the business
 * has one, and offsetting a payable against a receivable becomes impossible to
 * express.
 *
 * A role is a row in `party_role`, not a boolean column, because roles are open
 * (section 10 lists ServiceProvider, Partner, SalesRepresentative and
 * CustomRole beyond the three shipped here) and because a boolean column per
 * role turns every new role into a migration.
 */
export const PARTY_STATUSES = ['active', 'archived'];
/**
 * What a party *is*: a natural person or a legal entity.
 *
 * This is not a role and it never changes. It decides which document applies —
 * CPF for a person, CNPJ for a company — and that is the whole of its
 * behaviour here.
 */
export const PARTY_KINDS = ['person', 'company'];
/**
 * What a party is *to this business*. A party may hold several at once.
 *
 * Three of the seven that section 10 lists. The remaining four —
 * ServiceProvider, Partner, SalesRepresentative, CustomRole — are left out
 * deliberately: each needs its own fields and its own screens to mean anything,
 * and a role that exists as a checkbox with no behaviour behind it is a promise
 * the product does not keep. Adding one later is a row in this list plus a
 * check constraint, not a migration of anything already stored.
 *
 * `courier` é o quarto, e chegou na F098 pelo caminho oposto: ele não é um
 * papel novo do catálogo, é um papel que estava ESCONDIDO dentro de `carrier`.
 * Os dois são "quem leva a mercadoria" e param aí. Transportadora é quem leva
 * com a nota, e vai no grupo `transp` da NF-e; entregador é quem leva e VOLTA
 * COM O DINHEIRO, e é quem aparece no Acerto do motoboy. Com um papel só, quem
 * fazia frete aparecia devendo um dinheiro que nunca teve na mão.
 *
 * Uma pessoa pode ter os dois, e o caso é comum: o motoboy da casa que também
 * leva encomenda com nota.
 */
export const PARTY_ROLES = [
    'customer',
    'supplier',
    'carrier',
    'courier',
];
/**
 * Inscrição Estadual status.
 *
 * Three values, matching the fiscal indicator an NF-e will eventually need for
 * its recipient: contribuinte, contribuinte isento, não contribuinte. Named in
 * English here like everything else in the code; the screen shows the
 * Portuguese.
 *
 * It is a separate field from the IE number itself because "isento" and "has
 * no IE recorded yet" are different facts, and a null IE cannot tell them
 * apart. Getting that wrong misclassifies the recipient on a fiscal document,
 * which is a rejection at SEFAZ rather than a cosmetic error.
 */
export const STATE_REGISTRATION_STATUSES = [
    'taxpayer',
    'exempt',
    'non_taxpayer',
];
/**
 * What an address is for.
 *
 * `other` is present so that an address nobody has classified is still
 * storable. A required classification with no honest answer produces a field
 * everybody fills with the first option.
 */
export const ADDRESS_KINDS = ['billing', 'delivery', 'other'];
export const CONTACT_KINDS = ['phone', 'mobile', 'email', 'whatsapp'];
/**
 * The federative units, as the two-letter codes every Brazilian document uses.
 *
 * A fixed list rather than a table for the same reason units of measure are a
 * fixed list in the catalogue: there is nothing to administer. The set changes
 * by constitutional amendment, not by a customer's preference.
 */
export const UFS = [
    'AC',
    'AL',
    'AP',
    'AM',
    'BA',
    'CE',
    'DF',
    'ES',
    'GO',
    'MA',
    'MT',
    'MS',
    'MG',
    'PA',
    'PB',
    'PR',
    'PE',
    'PI',
    'RJ',
    'RN',
    'RS',
    'RO',
    'RR',
    'SC',
    'SP',
    'SE',
    'TO',
];
export const isUf = (value) => typeof value === 'string' && UFS.includes(value);
// ---------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------
/**
 * A document is ALWAYS a string. Master Specification section 11.
 *
 * These two functions are the whole reason `kind` exists on a party: they
 * dispatch to the CPF or the CNPJ implementation in `@aeris/organization`,
 * which is the "utilitário central de documentos fiscais" section 11 asks for.
 * Neither is reimplemented here.
 *
 * Note what `normalizeDocument` does NOT do: strip every non-alphanumeric
 * character. A CNPJ issued from July 2026 contains letters, and a normaliser
 * that removed everything unexpected would turn a typo into a value that then
 * passes validation. Each implementation removes only its own punctuation and
 * leaves anything else in place to be rejected.
 */
export const normalizeDocument = (kind, value) => kind === 'person' ? normalizeCpf(value) : normalizeCnpj(value);
export const isValidDocument = (kind, value) => kind === 'person' ? isValidCpf(value) : isValidCnpj(value);
/** For display only. Returns the input untouched when it is not valid. */
export const formatDocument = (kind, value) => kind === 'person' ? formatCpf(value) : formatCnpj(value);
// ---------------------------------------------------------------------------
// Address fields
// ---------------------------------------------------------------------------
/**
 * CEP — eight digits, stored without the hyphen.
 *
 * Format only. Whether `01310-100` is a real postal code, and which street it
 * names, is a question for the Correios, and the specification for this feature
 * deliberately does not call an external CEP service: an address form that
 * cannot be completed while the network is down is an address form that stops a
 * sale (Master Specification section 25 makes offline a supported state, not an
 * exception). Autofill is a convenience that can be added later without moving
 * a single stored value.
 */
export const normalizeCep = (cep) => cep.replace(/[\s-]/g, '');
export const isValidCepFormat = (cep) => /^[0-9]{8}$/.test(normalizeCep(cep));
export const formatCep = (cep) => {
    const normalized = normalizeCep(cep);
    if (!isValidCepFormat(normalized))
        return cep;
    return `${normalized.slice(0, 5)}-${normalized.slice(5)}`;
};
/**
 * IBGE municipality code — seven digits.
 *
 * Nullable, and format-checked only. It is what a fiscal document actually
 * carries for a municipality, so it is stored from the start rather than
 * derived later from a name that may be spelled three ways. Whether a given
 * code is the *correct* one for the município written next to it is a question
 * for the IBGE table, which the fiscal feature owns — the same division of
 * labour the catalogue applies to NCM.
 */
export const isValidIbgeMunicipalityCode = (code) => /^[0-9]{7}$/.test(code.trim());
// ---------------------------------------------------------------------------
// Contact values
// ---------------------------------------------------------------------------
/**
 * Telephone numbers are stored as digits only.
 *
 * `(11) 98765-4321`, `11987654321` and `11 98765 4321` are one number, and a
 * search box that fails to find the second when the first was typed is the
 * bug this prevents. The country code is not assumed or prepended: a supplier
 * abroad is rare but not impossible, and inventing a `+55` would corrupt one.
 */
export const normalizePhone = (value) => value.replace(/[\s()\-.]/g, '');
/** Lower-cased and trimmed. Nothing more: address syntax is checked by Zod. */
export const normalizeEmail = (value) => value.trim().toLowerCase();
export const normalizeContactValue = (kind, value) => (kind === 'email' ? normalizeEmail(value) : normalizePhone(value));
/**
 * The name the seeded walk-in customer carries, in one place.
 *
 * Portuguese because it is data a user reads, not code. Both the seed and any
 * later tenant provisioning use this constant, so the two cannot create a
 * `Consumidor Final` and a `Consumidor final` that no report can add together.
 */
export const SYSTEM_CUSTOMER_LEGAL_NAME = 'Consumidor Final';
/**
 * O nome de um cadastro nas listas — F211: "sempre nome - razão social".
 *
 * Toda tela mostrava `trade_name ?? legal_name`: o cliente importado da loja
 * GV aparecia só como "TOPTECH", e quem procurava pela razão social não se
 * achava na lista. Com os dois, fantasia primeiro — é o que o balcão fala.
 *
 * A mesma regra mora no banco, em `aeris.party_display_name` (migração 0114),
 * para as listas que já saem prontas do SQL. As duas têm de dizer a mesma
 * coisa; o teste de cada lado confere os mesmos casos.
 */
export const partyDisplayName = (party) => {
    const trade = party.tradeName?.trim() ?? '';
    if (trade === '' ||
        trade.toLowerCase() === party.legalName.trim().toLowerCase()) {
        return party.legalName;
    }
    return `${trade} — ${party.legalName}`;
};
/** Trimmed, or null when there is nothing left. A blank string is not a value. */
export const optionalText = (value) => {
    if (value === null || value === undefined)
        return null;
    const trimmed = value.trim();
    return trimmed.length === 0 ? null : trimmed;
};
/**
 * Inscrição Estadual, normalised to digits.
 *
 * Deliberately NOT validated beyond that. Each of the 27 federative units has
 * its own IE algorithm — different lengths, different weights, and São Paulo
 * has two schemes — and shipping 27 half-remembered algorithms would reject
 * valid registrations, which is far worse than accepting a wrong one. The
 * number is stored as given, digits only, and verified against SEFAZ by the
 * fiscal feature that actually needs it to be right.
 */
export const normalizeStateRegistration = (value) => value.replace(/[^0-9]/g, '');
//# sourceMappingURL=entities.js.map